import { useEffect, useRef, useState } from 'react'
import { trackUse } from '@/state/usage'
import { db, mutate } from '@/runtime'
import { personFor } from '@/lib/people'
import type { PersonResource } from '@/kinds/person/apply'
import { useAllTasks, usePeople, useProjects, useSuggestionsByIds } from '@/db/hooks'
import { quickAddParse, splitLines } from '@/kinds/task/parse'
import { KindError } from '@/kinds/kind'
import { useUi } from '@/state/ui'
import { useScope } from '@/state/scope'
import { useCurrentWorkspace, useWorkspaces } from '@/db/hooks'
import { ALL, DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { WS } from '@/db/ops'
import { dayChoices, fmtDateShort, fmtMinutes, todayLocal } from '@/lib/time'
import { Button, Sheet, cx } from './ui'
import { Mic } from 'lucide-react'
import { PrimeMark } from './PrimeMark'
import { SuggestionCard, useAssistantEnabled } from './Suggestions'
import { useAgentState } from '@/voice/agent'
import { useSendToPrime } from '@/os/AskPrime'
import { fileThis } from '@/lib/lead'
import { useFactoryEnabled } from '@/kinds/agentrun/components'
import { TaskForm } from '@/kinds/task/components'
import { DeferMenu } from './DeferMenu'
import type { TaskResource } from '@/kinds/task/apply'
import { Icon, iconText } from '@/lib/icons'
import { addConcepts } from '@/lib/learnCapture'
import type { ProjectResource } from '@/kinds/project/apply'

/** Fuzzy match "@proj" against active project titles: prefix, then substring, then initials. */
export const matchProject = (name: string, projects: ProjectResource[]): ProjectResource | undefined => {
  const q = name.toLowerCase()
  const active = projects.filter((p) => p.status.state === 'active')
  return (
    active.find((p) => p.spec.title.toLowerCase() === q) ??
    active.find((p) => p.spec.title.toLowerCase().startsWith(q)) ??
    active.find((p) => p.spec.title.toLowerCase().includes(q)) ??
    active.find(
      (p) =>
        p.spec.title
          .toLowerCase()
          .split(/\s+/)
          .map((w) => w[0])
          .join('') === q,
    )
  )
}

/**
 * "When?" after something is added with no day: one tap gives it one (and
 * takes it out of the inbox, as a day decides it), all undoable as one step.
 * Ignoring it changes nothing.
 */
const WhenChips = ({
  task,
  onPick,
  onPlaced,
}: {
  task: TaskResource
  onPick: () => void
  onPlaced: (label: string) => void
}) => {
  const today = todayLocal()
  const options = dayChoices(today)
  const give = (label: string, day: string) =>
    void mutate
      .grouped(async () => {
        await mutate.action(task.id, 'defer', { scheduled: day })
        await mutate.action(task.id, 'move', { state: 'next' })
      })
      .then(() =>
        onPlaced(
          label === 'This weekend' || label === 'Next week' ? `${label}, ${fmtDateShort(day, today)}` : label,
        ),
      )
  return (
    <div
      // wraps rather than scrolling, so every choice (Pick… too) is in sight on a phone
      className="flex flex-wrap items-center gap-1.5 mt-2"
      data-testid="quick-add-when"
    >
      <span className="text-xs text-muted shrink-0">When?</span>
      {options.map(([label, day]) => (
        <button
          key={label}
          type="button"
          onClick={() => give(label, day)}
          className="shrink-0 rounded-full bg-raised border border-line px-3 min-h-8 text-xs hover:bg-line"
        >
          {label}
        </button>
      ))}
      <button
        type="button"
        onClick={onPick}
        className="shrink-0 rounded-full bg-raised border border-line px-3 min-h-8 text-xs hover:bg-line"
      >
        Pick…
      </button>
    </div>
  )
}

/** Optional triage applied at capture time so an item can skip the inbox. */
export interface AddOptions {
  /** where it lands; default inbox (or next when a date is set) */
  state?: 'next' | 'someday'
  scheduled?: string
  projectId?: string
  /** workspace slug; `+slug` in the text wins */
  workspace?: string
}

export const addTaskFromText = async (text: string, projects: ProjectResource[], opts: AddOptions = {}) => {
  const parsed = quickAddParse(text)
  if (!parsed.spec.title) return null
  trackUse('action:capture')
  const spec = { ...parsed.spec }
  if (opts.scheduled && !spec.scheduled) spec.scheduled = opts.scheduled
  const labels = { ...(parsed.labels ?? {}) }
  const ws = parsed.workspace ?? opts.workspace
  if (ws && ws !== ALL) labels[WS] = ws
  // @name is a person when it is someone's handle or name, else a project
  const people = (await db.resources.where('kind').equals('Person').toArray()) as PersonResource[]
  const who = personFor(people, parsed.project)
  const r = await mutate.create('Task', spec, labels)
  const projectId =
    opts.projectId ?? (parsed.project && !who ? matchProject(parsed.project, projects)?.id : undefined)
  if (projectId) await mutate.setProject(r.id, projectId)
  if (who) await mutate.setEdge(r.id, who.id, 'references')
  if (opts.state && r.status.state !== opts.state) await mutate.action(r.id, 'move', { state: opts.state })
  return r
}

const KEY: [string, string][] = [
  ['#area:home', 'label'],
  ['@project', 'project or person'],
  ['!1–3', 'priority'],
  ['^fri', 'due'],
  ['>tomorrow', 'scheduled'],
  ['~20', 'minutes'],
  ['+dev', 'suite'],
]

/**
 * What the line will make, as it is typed: each thing the shorthand was read
 * as, in words ("Tomorrow", "With Mum", "area:home"), so nothing is a guess.
 * The syntax key stays under it for what is left, quieter once typing starts.
 */
const SyntaxKey = ({ text, projects }: { text: string; projects: ProjectResource[] }) => {
  const parsed = quickAddParse(text.split('\n')[0] ?? '')
  const who = personFor(usePeople(), parsed.project)
  const project = parsed.project && !who ? matchProject(parsed.project, projects) : undefined
  const suites = useWorkspaces()
  const today = todayLocal()
  const active: Record<string, string | undefined> = {
    '#area:home':
      parsed.labels &&
      Object.entries(parsed.labels)
        .map(([k, v]) => `${k}:${v}`)
        .join(' '),
    '@project': parsed.project
      ? who
        ? `With ${who.spec.name}`
        : project
          ? project.spec.title
          : `No project or person called ${parsed.project}`
      : undefined,
    '!1–3': parsed.spec.priority !== undefined ? `Priority ${parsed.spec.priority}` : undefined,
    '^fri': parsed.spec.due ? `Due ${fmtDateShort(parsed.spec.due, today)}` : undefined,
    '>tomorrow': parsed.spec.scheduled ? fmtDateShort(parsed.spec.scheduled, today) : undefined,
    '~20': parsed.spec.estimate_minutes ? fmtMinutes(parsed.spec.estimate_minutes) : undefined,
    '+dev': parsed.workspace
      ? (suites.find((w) => w.spec.slug === parsed.workspace)?.spec.name ??
        `No suite called ${parsed.workspace}`)
      : undefined,
  }
  const understood = KEY.filter(([token]) => active[token])
  const rest = KEY.filter(([token]) => !active[token])
  return (
    <div className="mt-1.5" data-testid="syntax-key">
      {understood.length > 0 && (
        <ul
          className="flex flex-wrap gap-1.5 mb-1.5"
          aria-label="What it understood"
          data-testid="quick-add-understood"
        >
          {understood.map(([token]) => (
            <li
              key={token}
              className="rounded-full bg-accent-soft text-accent-text px-2.5 min-h-6 inline-flex items-center text-xs"
            >
              {active[token]}
            </li>
          ))}
        </ul>
      )}
      <div
        className={cx(
          'flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] leading-4 font-mono',
          text.trim() && 'opacity-60',
        )}
        aria-label="Syntax key"
      >
        {rest.map(([token, meaning]) => (
          <span key={token} className="text-faint">
            {token} <span className="font-sans">{meaning}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

type Mode = 'add' | 'ai'
const readMode = (): Mode => {
  try {
    return localStorage.getItem('quick_add_mode') === 'ai' ? 'ai' : 'add'
  } catch {
    return 'add'
  }
}

/**
 * One field, Enter creates a Task in inbox and keeps the field open. A
 * multi-line paste becomes a brain dump: one task per line after confirming.
 * AI mode sends the text to Prime instead (dictating through the day), to file;
 * the sheet goes and Prime's thread opens with its answer.
 */
/** Remounts on every open so shared text (share sheet, Shortcuts) seeds a fresh sheet. */
export const QuickAdd = (props: { projectId?: string }) => {
  const seq = useUi((s) => s.quickAddSeq)
  const scopeQuickAdd = useUi((s) => s.scopeQuickAdd)
  const yields = useUi((s) => !props.projectId && s.quickAddScoped > 0)
  const scoped = !!props.projectId
  // both follow the one open flag, so the shell's sheet stands down while a project page has its own
  useEffect(() => {
    if (!scoped) return
    scopeQuickAdd(1)
    return () => scopeQuickAdd(-1)
  }, [scoped, scopeQuickAdd])
  if (yields) return null
  return <QuickAddInner key={seq} {...props} />
}

const QuickAddInner = ({ projectId }: { projectId?: string }) => {
  const open = useUi((s) => s.quickAddOpen)
  const setOpen = useUi((s) => s.setQuickAdd)
  const [text, setText] = useState(() => useUi.getState().takeQuickAddText() ?? '')
  const [dump, setDump] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // what was added last (its title, or that a brain dump went in), said once as a receipt
  const [added, setAdded] = useState<string | null>(null)
  // the last thing added with no day: offered a day, once, never pressed on
  const [undated, setUndated] = useState<TaskResource | null>(null)
  const [placed, setPlaced] = useState<string | null>(null)
  const [pickingDay, setPickingDay] = useState(false)
  // triage chips: sticky while the sheet is open (handy for a brain dump), reset on close
  const [state, setState] = useState<'next' | 'someday' | null>(null)
  const [scheduled, setScheduled] = useState<string | null>(null)
  const [pickedProject, setPickedProject] = useState<string | null>(null)
  const [picking, setPicking] = useState<'date' | 'project' | 'workspace' | null>(null)
  const scope = useScope((s) => s.ws)
  const workspaces = useWorkspaces().filter((w) => !w.status.archived_at)
  const [pickedWs, setPickedWs] = useState<string | null>(null)
  const projects = useProjects() ?? []
  // AI mode is Prime: offered when Prime can run (the factory) or the quick chat can
  // take the words from its start screen (an assistant); it sticks across opens
  // (voice dictation all day), per device
  const assistantOn = useAssistantEnabled()
  const factory = useFactoryEnabled()
  const assistant = assistantOn || factory
  const voice = useAgentState()
  const sendToPrime = useSendToPrime()
  const [mode, setModeState] = useState<Mode>(readMode)
  const setMode = (m: Mode) => {
    setModeState(m)
    try {
      localStorage.setItem('quick_add_mode', m)
    } catch {
      /* private mode */
    }
    ref.current?.focus()
  }
  const ai = mode === 'ai' && !!assistant
  // Concept target: where Learn is on, a line becomes a Concept in the suite instead of a task
  const current = useCurrentWorkspace()
  const learnOn =
    scope === ALL
      ? workspaces.some((w) => w.spec.packs.includes('learn'))
      : !!current?.spec.packs.includes('learn')
  const [asConcept, setAsConcept] = useState(() => useUi.getState().takeQuickAddConcept())
  const concept = asConcept && learnOn && !ai
  const [thinking, setThinking] = useState(false)
  // Advanced: every field, prefilled from whatever shorthand is typed so far
  const [advanced, setAdvanced] = useState(false)
  const [askedIds, setAskedIds] = useState<string[]>([])
  const [askMsg, setAskMsg] = useState<string | null>(null)
  const asked = useSuggestionsByIds(askedIds).filter((x) => x.status.state === 'pending')
  const allTasks = useAllTasks() ?? []
  const titleOf = (id: string) => allTasks.find((t) => t.id === id)?.spec.title
  const activeProjects = projects
    .filter((p) => p.status.state === 'active' || p.status.state === 'paused')
    .sort((a, b) => a.spec.title.localeCompare(b.spec.title))
  const ref = useRef<HTMLTextAreaElement>(null)
  const today = todayLocal()
  const opts = (): AddOptions => ({
    ...(scope !== ALL ? { workspace: scope } : pickedWs ? { workspace: pickedWs } : {}),
    ...(state ? { state } : {}),
    ...(scheduled ? { scheduled } : {}),
    ...((projectId ?? pickedProject) ? { projectId: (projectId ?? pickedProject)! } : {}),
  })

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => ref.current?.focus(), 30)
      return () => clearTimeout(t)
    }
  }, [open])
  const close = () => {
    setOpen(false)
    setDump(null)
    setAskedIds([])
    setAskMsg(null)
    setError(null)
    setAdded(null)
    setState(null)
    setScheduled(null)
    setPickedProject(null)
    setPickedWs(null)
    setPicking(null)
    setAsConcept(false)
  }
  const toggleState = (s: 'next' | 'someday') => setState((cur) => (cur === s ? null : s))
  const pickDate = (d: string | null) => {
    setScheduled(d)
    if (d && state === 'someday') setState(null)
    setPicking(null)
    ref.current?.focus()
  }

  // AI mode is Prime (the lead, lib/lead.ts): the words go to its thread as the
  // owner's message, framed as something to file ("File this: …"), the sheet
  // closes and the thread opens with the reply on its way, so a follow-up is
  // just the next line. With no Prime running they wait on its start screen.
  const ask = async () => {
    const prompt = text.trim()
    if (!prompt || thinking) return
    setThinking(true)
    setAskMsg(null)
    try {
      // Prime sees every workspace; the sheet's is named unless it is the default one
      const picked = scope === ALL ? pickedWs : scope
      const ws = picked && picked !== DEFAULT_WORKSPACE_SLUG ? picked : undefined
      await sendToPrime(fileThis(prompt, ws))
      setText('')
      close()
    } catch (e) {
      setAskMsg(e instanceof Error ? e.message : 'The assistant is unavailable')
    } finally {
      setThinking(false)
    }
  }

  const submit = async () => {
    if (ai) return ask()
    const lines = splitLines(text)
    if (!lines.length) return
    if (concept) {
      const previous = text
      setText('')
      setError(null)
      try {
        const made = await addConcepts(lines, opts().workspace)
        setAdded(made.length === 1 ? made[0]!.spec.name : 'every line')
        setPlaced(null)
        setUndated(null)
      } catch (e) {
        setText(previous)
        setError(e instanceof KindError ? e.message : 'Could not save')
      }
      return
    }
    if (lines.length > 1) {
      setDump(lines)
      return
    }
    // clear synchronously so keystrokes typed while saving are never wiped
    const previous = text
    setText('')
    setError(null)
    try {
      const r = (await addTaskFromText(lines[0]!, projects, opts())) as TaskResource | null
      setAdded(r?.spec.title ?? lines[0]!)
      setPlaced(null)
      // only when nothing about it was decided: no day, no Next or Someday, no project
      const o = opts()
      const decided = !!(o.state || o.scheduled || o.projectId)
      setUndated(r && !decided && !r.spec.scheduled && !r.spec.due ? r : null)
    } catch (e) {
      setText(previous)
      setError(e instanceof KindError ? e.message : 'Could not save')
    }
  }

  const confirmDump = async () => {
    for (const line of dump ?? []) await addTaskFromText(line, projects, opts())
    setAdded('every line')
    setDump(null)
    setText('')
    ref.current?.focus()
  }

  if (advanced && open)
    return (
      <TaskForm
        initialText={text}
        initialProjectId={projectId ?? pickedProject ?? undefined}
        onDone={() => {
          setAdvanced(false)
          close()
        }}
      />
    )
  return (
    <Sheet open={open} onClose={close} title={projectId ? 'Add to project' : 'Add'}>
      {dump ? (
        <div>
          <p className="text-sm text-fg-2 mb-3">
            Create <strong>{dump.length}</strong> tasks, one per line?
          </p>
          <ul className="text-sm text-muted mb-4 max-h-48 overflow-y-auto space-y-1 list-disc pl-5">
            {dump.map((l, i) => (
              <li key={i} className="truncate">
                {l}
              </li>
            ))}
          </ul>
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setDump(null)}>Back</Button>
            <Button variant="primary" onClick={() => void confirmDump()}>
              Create {dump.length}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {(assistant || voice.available) && (
            <div className="flex gap-1 mb-2" role="tablist" aria-label="Mode">
              <Button
                variant={ai ? 'chip' : 'chipOn'}
                onClick={() => setMode('add')}
                data-testid="quick-add-mode-add"
              >
                Add
              </Button>
              {assistant && (
                <Button
                  variant={ai ? 'chipOn' : 'chip'}
                  onClick={() => setMode('ai')}
                  data-testid="quick-add-mode-ai"
                >
                  <PrimeMark size={13} className="mr-1" aria-hidden /> Prime
                </Button>
              )}
              {voice.available && (
                // a spoken conversation (the quick chat's brain): the sheet goes, the bar above the tab bar takes over
                <Button
                  variant="chip"
                  onClick={() => {
                    close()
                    void voice.start().catch(() => {})
                  }}
                  data-testid="quick-add-mode-prime"
                >
                  <Mic size={13} className="mr-1" /> Talk
                </Button>
              )}
            </div>
          )}
          <textarea
            ref={ref}
            rows={ai ? 3 : 2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void submit()
              }
            }}
            placeholder={
              ai
                ? 'Tell Prime what’s going on and it files it.'
                : concept
                  ? 'A concept: its name, or Name: what it is in a line'
                  : "What's on your mind?"
            }
            className={cx('w-full resize-none', ai && 'text-base')}
            aria-label="Quick add"
            data-testid="quick-add-input"
          />
          {!ai && !concept && <SyntaxKey text={text} projects={projects} />}
          {error && <p className="text-xs text-rose-300 mt-1">{error}</p>}
          {ai && askMsg && <p className="text-xs text-muted mt-1">{askMsg}</p>}
          {ai && asked.length > 0 && (
            <ul className="flex flex-col gap-1.5 mt-2" data-testid="quick-add-suggestions">
              {asked.map((x) => (
                <SuggestionCard key={x.id} s={x} titleOf={titleOf} />
              ))}
            </ul>
          )}
          <div className={cx('flex flex-wrap gap-1 mt-2', ai && 'hidden')} aria-label="Where it goes">
            {learnOn && !projectId && (
              <Button
                variant={concept ? 'chipOn' : 'chip'}
                onClick={() => {
                  setAsConcept(!concept)
                  setPicking(null)
                  ref.current?.focus()
                }}
                data-testid="quick-add-concept"
              >
                Concept
              </Button>
            )}
            {!concept && (
              <>
                <Button variant={state === 'next' ? 'chipOn' : 'chip'} onClick={() => toggleState('next')}>
                  Next
                </Button>
                <Button
                  variant={state === 'someday' ? 'chipOn' : 'chip'}
                  onClick={() => toggleState('someday')}
                >
                  Someday
                </Button>
                <Button
                  variant={scheduled ? 'chipOn' : 'chip'}
                  onClick={() => setPicking(picking === 'date' ? null : 'date')}
                >
                  {scheduled ? fmtDateShort(scheduled, today) : 'Schedule'}
                </Button>
              </>
            )}
            {scope === ALL && (
              <Button
                variant={pickedWs ? 'chipOn' : 'chip'}
                onClick={() => setPicking(picking === 'workspace' ? null : 'workspace')}
                data-testid="quick-add-ws"
              >
                {pickedWs
                  ? `${iconText(workspaces.find((w) => w.spec.slug === pickedWs)?.spec.icon)} ${workspaces.find((w) => w.spec.slug === pickedWs)?.spec.name ?? pickedWs}`
                  : 'Suite'}
              </Button>
            )}
            {!projectId && !concept && (
              <Button
                variant={pickedProject ? 'chipOn' : 'chip'}
                onClick={() => setPicking(picking === 'project' ? null : 'project')}
              >
                {pickedProject
                  ? (projects.find((p) => p.id === pickedProject)?.spec.title ?? 'Project')
                  : 'Project'}
              </Button>
            )}
          </div>
          {picking === 'date' && (
            <div className="flex flex-wrap items-center gap-1 mt-2 fade-in">
              {/* the same days as the When? chips and a task's Reschedule */}
              {dayChoices(today).map(([label, day]) => (
                <Button key={label} variant="chip" onClick={() => pickDate(day)}>
                  {label}
                </Button>
              ))}
              <input
                type="date"
                className="min-h-8 py-1 text-sm"
                value={scheduled ?? ''}
                onChange={(e) => e.target.value && pickDate(e.target.value)}
                aria-label="Pick a day"
              />
              {scheduled && (
                <Button variant="chip" onClick={() => pickDate(null)}>
                  Clear
                </Button>
              )}
            </div>
          )}
          {picking === 'workspace' && (
            <div className="flex flex-col gap-1 mt-2 fade-in">
              {workspaces.map((w) => (
                <Button
                  key={w.id}
                  className="justify-start"
                  variant={pickedWs === w.spec.slug ? 'chipOn' : 'ghost'}
                  onClick={() => {
                    setPickedWs(w.spec.slug)
                    setPicking(null)
                    ref.current?.focus()
                  }}
                >
                  <Icon value={w.spec.icon} /> {w.spec.name}
                </Button>
              ))}
            </div>
          )}
          {picking === 'project' && (
            <div className="flex flex-col gap-1 mt-2 max-h-40 overflow-y-auto fade-in">
              <Button
                className="justify-start"
                variant={pickedProject ? 'ghost' : 'chipOn'}
                onClick={() => {
                  setPickedProject(null)
                  setPicking(null)
                }}
              >
                No project
              </Button>
              {activeProjects.map((p) => (
                <Button
                  key={p.id}
                  className="justify-start"
                  variant={pickedProject === p.id ? 'chipOn' : 'ghost'}
                  onClick={() => {
                    setPickedProject(p.id)
                    setPicking(null)
                    ref.current?.focus()
                  }}
                >
                  {p.spec.title}
                </Button>
              ))}
              {!activeProjects.length && <p className="text-xs text-faint px-1">No projects yet.</p>}
            </div>
          )}
          {!ai && undated && !picking && (
            <WhenChips
              task={undated}
              onPick={() => setPickingDay(true)}
              onPlaced={(label) => {
                setPlaced(`${undated.spec.title}: ${label}`)
                setUndated(null)
              }}
            />
          )}
          {!ai && placed && !undated && (
            <p className="text-xs text-accent-text mt-2 truncate" data-testid="quick-add-placed">
              {placed}
            </p>
          )}
          {pickingDay && undated && (
            <DeferMenu
              task={undated}
              open
              onClose={() => {
                const id = undated.id
                setPickingDay(false)
                setUndated(null)
                // a day picked here decides it too: out of the inbox, like the chips
                void db.resources.get(id).then((t) => {
                  const task = t as TaskResource | undefined
                  if (task?.spec.scheduled && task.status.state === 'inbox')
                    void mutate.action(id, 'move', { state: 'next' })
                })
              }}
            />
          )}
          <div className="flex items-center justify-between mt-2 gap-2">
            {/* hints are phrases set apart by space, never joined by dots */}
            <span className="min-w-0 flex flex-wrap gap-x-3 text-xs text-faint" data-testid="quick-add-hint">
              {ai ? (
                <span>{thinking ? 'Sending…' : 'Prime files it and opens the thread'}</span>
              ) : concept && !added ? (
                <span>Enter adds a concept</span>
              ) : added ? (
                <span className="truncate max-w-full">
                  Added {added === 'every line' ? 'every line' : `“${added}”`}
                </span>
              ) : !concept && (state || scheduled || pickedProject) ? (
                <span>Skips the inbox</span>
              ) : (
                <>
                  <span>Enter adds</span>
                  <span>Paste lines for a brain dump</span>
                </>
              )}
            </span>
            <div className="flex gap-2">
              {!ai && !concept && (
                <Button
                  onClick={() => setAdvanced(true)}
                  data-testid="quick-add-advanced"
                  title="Every field: dates, estimate, energy, labels, checklist, recurrence"
                >
                  Advanced
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => void submit()}
                disabled={ai && (thinking || !text.trim())}
                data-testid="quick-add-submit"
              >
                {ai ? (thinking ? '…' : 'Send') : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Sheet>
  )
}
